import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { 
  Save, Eye, EyeOff, ChevronDown, ChevronRight, 
  FileText, AlertCircle, Check, Loader2, RotateCcw, Download,
  Sparkles, Wand2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// Default manual structure based on the template
const DEFAULT_MANUAL_STRUCTURE = [
  {
    id: 'family-wealth-creation',
    title: '1. Family Wealth Creation',
    sections: [
      {
        id: 'origin-story',
        title: '1.1 Origin Story and Key Milestones',
        prompt: 'Summarise how the family built its wealth and the key milestones.',
        content: ''
      },
      {
        id: 'primary-wealth-drivers',
        title: '1.2 Primary Wealth Drivers',
        prompt: 'Explain the split between operating businesses and investments, and what that means for risk and liquidity.',
        content: ''
      },
      {
        id: 'concentration-risks',
        title: '1.3 Concentration Risks and Legacy Assets',
        prompt: 'Legacy assets the family intends to keep and why. Current or planned risk mitigations.',
        content: ''
      },
      {
        id: 'lessons-learned',
        title: '1.4 Lessons Learned and Family Narrative',
        prompt: 'Behaviours that built wealth, and behaviours that eroded it. Stories the next generation should know, including successes and stumbles.',
        content: ''
      }
    ]
  },
  {
    id: 'family-purpose',
    title: '2. Family Purpose',
    sections: [
      {
        id: 'values-principles',
        title: '2.1 Values and Decision Principles',
        prompt: 'Core values with a one line behavioural definition for each.',
        content: ''
      },
      {
        id: 'mission-objectives',
        title: '2.2 Mission and Objectives',
        prompt: 'A one paragraph mission statement that explains why the family office exists.',
        content: ''
      },
      {
        id: 'family-council',
        title: '2.3 Family Council Charter and Meeting Rhythm',
        prompt: 'Council members, chair and meeting frequency. Decision rules and how minutes are kept.',
        content: ''
      },
      {
        id: 'philanthropy',
        title: '2.4 Philanthropy Priorities and Impact Themes',
        prompt: 'Focus areas and the reasons behind them. Budget approach, such as a fixed percentage or an opportunistic allocation. Giving vehicles, such as a foundation, donor advised fund or direct giving.',
        content: ''
      },
      {
        id: 'next-gen-education',
        title: '2.5 Next Generation Education and Engagement',
        prompt: 'Who is in scope now and next, and their roles and readiness. A learning plan that covers financial literacy, governance and work experience. Guardrails that balance responsibility and entitlement.',
        content: ''
      },
      {
        id: 'culture-diagnostic',
        title: '2.6 Culture and Values Diagnostic',
        prompt: 'Where the family sits today on the United vs Conflicted and Create vs Consume axes, and the target position for the next 12 to 24 months. Two behaviours to keep and two to change.',
        content: ''
      }
    ]
  },
  {
    id: 'family-org-chart',
    title: '3. Family Organisational Chart',
    sections: [
      {
        id: 'roles-responsibilities',
        title: '3.1 Roles and Responsibilities (RACI Summary)',
        prompt: 'Key activities and who is Responsible, Accountable, Consulted and Informed. Any single points of failure and the named back up for each.',
        content: ''
      },
      {
        id: 'meeting-rhythm',
        title: '3.2 Meeting Rhythm and Information Flow',
        prompt: 'Weekly, monthly and quarterly meetings, including attendees and expected outputs. What reports are produced, when they land, and who reviews them.',
        content: ''
      },
      {
        id: 'succession-overlay',
        title: '3.3 Succession Overlay (Backups for Each Role)',
        prompt: 'Who steps in for each key role for the first 72 hours, for 30 days, and for the long term.',
        content: ''
      }
    ]
  },
  {
    id: 'entity-purposes',
    title: '4. Entity Purposes and Structure',
    sections: [
      {
        id: 'structure-overview',
        title: '4.1 Structure Overview (How Entities Fit Together)',
        prompt: 'A current structure chart, including trustees, beneficiaries and owners. How profits and distributions typically flow.',
        content: ''
      },
      {
        id: 'holding-companies',
        title: '4.2 Holding and Operating Companies Purpose and Scope',
        prompt: 'Material contracts or licences that should be tracked.',
        content: ''
      },
      {
        id: 'investment-trusts',
        title: '4.3 Investment Trusts Mandates and Distribution Practices',
        prompt: 'A one paragraph mandate covering risk, return, horizon and constraints. Distribution approach and timing, including planning notes such as related party rules.',
        content: ''
      },
      {
        id: 'super-structures',
        title: '4.4 Superannuation and Retirement Structures Strategy and Nominations',
        prompt: 'Trustee and director details, and the investment strategy at a glance. Beneficiaries, binding nominations and review dates.',
        content: ''
      },
      {
        id: 'dormant-entities',
        title: '4.5 Dormant and Legacy Entities Status and Rationale',
        prompt: 'Why each entity is retained, and any costs or risks. The plan to wind up, repurpose or keep.',
        content: ''
      }
    ]
  },
  {
    id: 'operating-businesses',
    title: '5. Operating Businesses',
    sections: [
      {
        id: 'business-model',
        title: '5.1 Business Model, KPIs, Contracts, Cash Cycle and Risks',
        prompt: 'A summary of the operating business(es) including key performance indicators, material contracts, cash conversion cycle and principal risks.',
        content: ''
      },
      {
        id: 'governance-interfaces',
        title: '5.2 Governance Interfaces (Board, Advisory and Reporting)',
        prompt: 'Board or advisory composition, meeting cadence and reserved matters.',
        content: ''
      },
      {
        id: 'capital-allocation',
        title: '5.3 Capital Allocation and Dividend Policy',
        prompt: 'Dividend policy and the balance between reinvestment and distribution.',
        content: ''
      }
    ]
  },
  {
    id: 'banking-treasury',
    title: '6. Banking, Treasury and Financial Access',
    sections: [
      {
        id: 'treasury-overview',
        title: '6.1 Overview',
        prompt: 'Target cash buffers by entity and who monitors them. Payment approval thresholds and the back up approver. Who holds two factor devices and the recovery process if a device is lost. When surplus cash is swept and where it goes.',
        content: ''
      },
      {
        id: 'treasury-notes',
        title: '6.2 Notes and Guidance',
        prompt: 'Cash buffer rules and the exception process. Payment approvals aligned to delegations and how urgent exceptions are documented. Access control and recovery paths, including bank relationship manager and platform admin. Owners for the weekly liquidity snapshot and the month end cashflow review.',
        content: ''
      }
    ]
  },
  {
    id: 'investment-strategy',
    title: '7. Investment Strategy',
    sections: [
      {
        id: 'investment-overview',
        title: '7.1 Strategy Overview',
        prompt: 'Brief overview of the investment strategy including asset allocation, risk tolerance, time horizon and any specific mandates or constraints.',
        content: ''
      }
    ]
  },
  {
    id: 'compliance-governance',
    title: '8. Compliance and Governance',
    sections: [
      {
        id: 'compliance-overview',
        title: '8.1 Compliance Overview',
        prompt: 'Tax calendar highlights and sign off responsibilities. Key obligations across entities, including companies, trusts, superannuation and payroll.',
        content: ''
      },
      {
        id: 'audit-assurance',
        title: '8.2 Audit and Assurance',
        prompt: 'Audit and assurance scope, timetable and provider contacts.',
        content: ''
      },
      {
        id: 'policies-procedures',
        title: '8.3 Policies and Procedures',
        prompt: 'Policies and procedures, with owners and review cadence. Delegations of authority summary, storage location and last review date.',
        content: ''
      },
      {
        id: 'risk-controls',
        title: '8.4 Risk Register and Controls',
        prompt: 'Top risks and controls for operational, market, counterparty, and cyber risks. How exceptions are logged.',
        content: ''
      }
    ]
  },
  {
    id: 'succession-continuity',
    title: '9. Succession and Continuity Plan',
    sections: [
      {
        id: 'handover-rhythm',
        title: '9.1 Handover and Review Rhythm',
        prompt: 'Handover milestones and success measures. Coaching and education plan for incoming leaders. Quarterly and annual review cadence.',
        content: ''
      }
    ]
  },
  {
    id: 'general-notes',
    title: '10. General Notes',
    sections: [
      {
        id: 'operating-notes',
        title: '10.1 Operating Notes',
        prompt: 'Weekly rhythm, owners and the definition of done for each week. Expense rules, including limits, receipt requirements and approval windows. Recurring payments calendar and reminder approach.',
        content: ''
      },
      {
        id: 'systems-inventory',
        title: '10.2 Systems and Documents',
        prompt: 'Systems inventory with owner and renewal or maintenance dates. Document storage conventions and version control approach.',
        content: ''
      },
      {
        id: 'onboarding-offboarding',
        title: '10.3 Onboarding and Offboarding',
        prompt: 'Onboarding and offboarding steps and where checklists are stored. Special instructions and responsible owners.',
        content: ''
      }
    ]
  }
];

// Editable Section Component
function EditableSection({ section, onChange, isHidden, onToggleHide, onAISuggest, isGenerating }) {
  const [isFocused, setIsFocused] = useState(false);
  
  const hasContent = section.content && section.content.trim().length > 0;
  
  return (
    <div className={cn(
      "border border-[#E6E0D9] rounded-lg mb-4 transition-all",
      isHidden && "opacity-50"
    )}>
      <div className="flex items-center justify-between px-4 py-3 bg-[#FAF8F5] border-b border-[#E6E0D9] rounded-t-lg">
        <h4 className="font-medium text-[#2C2420]">{section.title}</h4>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onAISuggest(section)}
            disabled={isGenerating}
            className={cn(
              "p-1.5 rounded-md transition-colors flex items-center gap-1",
              "text-[#4A3B32] hover:bg-[#4A3B32]/10",
              isGenerating && "opacity-50 cursor-wait"
            )}
            title="Generate AI suggestion"
            data-testid={`ai-suggest-${section.id}`}
          >
            {isGenerating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={onToggleHide}
            className={cn(
              "p-1.5 rounded-md transition-colors",
              isHidden 
                ? "text-[#8C8078] hover:text-[#4A3B32] hover:bg-[#4A3B32]/5" 
                : "text-[#4A3B32] hover:bg-[#4A3B32]/10"
            )}
            title={isHidden ? "Show section" : "Hide section"}
            data-testid={`hide-section-${section.id}`}
          >
            {isHidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      
      {!isHidden && (
        <div className="p-4">
          <div className="relative">
            <textarea
              value={section.content}
              onChange={(e) => onChange(section.id, e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder={section.prompt}
              rows={4}
              className={cn(
                "w-full px-3 py-2 border rounded-md resize-y min-h-[100px] transition-colors",
                "placeholder:text-[#B8AFA8] placeholder:italic",
                "focus:outline-none focus:ring-2 focus:ring-[#4A3B32]/20 focus:border-[#4A3B32]",
                hasContent ? "border-[#D9D6D0] bg-white" : "border-[#E6E0D9] bg-[#FDFCFB]"
              )}
              data-testid={`section-input-${section.id}`}
            />
            {!hasContent && !isFocused && (
              <div className="absolute top-2 right-2">
                <span className="text-xs text-[#B8AFA8] bg-[#F5F1EB] px-2 py-0.5 rounded">
                  Prompt
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Chapter Component with expand/collapse
function Chapter({ chapter, sections, hiddenSections, onSectionChange, onToggleHide, isExpanded, onToggleExpand, onAISuggest, generatingSection }) {
  const visibleSections = chapter.sections.filter(s => !hiddenSections.includes(s.id));
  const hiddenCount = chapter.sections.length - visibleSections.length;
  const completedCount = chapter.sections.filter(s => sections[s.id]?.content?.trim()).length;
  
  return (
    <div className="mb-6 border border-[#E6E0D9] rounded-xl overflow-hidden bg-white">
      <button
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between px-5 py-4 bg-[#F9F7F5] hover:bg-[#F5F1EB] transition-colors"
        data-testid={`chapter-toggle-${chapter.id}`}
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-[#4A3B32]" />
          ) : (
            <ChevronRight className="w-5 h-5 text-[#4A3B32]" />
          )}
          <h3 className="text-lg font-heading text-[#2C2420]">{chapter.title}</h3>
        </div>
        <div className="flex items-center gap-3">
          {hiddenCount > 0 && (
            <span className="text-xs text-[#8C8078] bg-[#E6E0D9] px-2 py-0.5 rounded">
              {hiddenCount} hidden
            </span>
          )}
          <span className={cn(
            "text-xs px-2 py-0.5 rounded",
            completedCount === chapter.sections.length 
              ? "text-green-700 bg-green-100" 
              : "text-[#8C8078] bg-[#E6E0D9]"
          )}>
            {completedCount}/{chapter.sections.length} complete
          </span>
        </div>
      </button>
      
      {isExpanded && (
        <div className="p-5">
          {chapter.sections.map((section) => (
            <EditableSection
              key={section.id}
              section={{
                ...section,
                content: sections[section.id]?.content || ''
              }}
              onChange={onSectionChange}
              isHidden={hiddenSections.includes(section.id)}
              onToggleHide={() => onToggleHide(section.id)}
              onAISuggest={onAISuggest}
              isGenerating={generatingSection === section.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ClientContinuityManual() {
  const { client, clientId } = useOutletContext();
  const { token } = useAuth();
  
  const [sections, setSections] = useState({});
  const [hiddenSections, setHiddenSections] = useState([]);
  const [expandedChapters, setExpandedChapters] = useState(['family-wealth-creation']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saved' | 'error' | null
  const [hasChanges, setHasChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [generatingSection, setGeneratingSection] = useState(null);

  // Load manual data
  useEffect(() => {
    const fetchManual = async () => {
      if (!token || !clientId) return;
      try {
        const response = await api.get(token, `/clients/${clientId}/continuity-manual`);
        if (response.data) {
          setSections(response.data.sections || {});
          setHiddenSections(response.data.hiddenSections || []);
          setLastSaved(response.data.updatedAt);
        }
      } catch (error) {
        // If 404, it's a new manual - use defaults
        if (error.response?.status !== 404) {
          console.error('Failed to load continuity manual:', error);
        }
      }
      setLoading(false);
    };
    fetchManual();
  }, [token, clientId]);

  // Handle section content change
  const handleSectionChange = useCallback((sectionId, content) => {
    setSections(prev => ({
      ...prev,
      [sectionId]: { content }
    }));
    setHasChanges(true);
    setSaveStatus(null);
  }, []);

  // Toggle section visibility
  const handleToggleHide = useCallback((sectionId) => {
    setHiddenSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
    setHasChanges(true);
    setSaveStatus(null);
  }, []);

  // Toggle chapter expand/collapse
  const handleToggleExpand = useCallback((chapterId) => {
    setExpandedChapters(prev => 
      prev.includes(chapterId)
        ? prev.filter(id => id !== chapterId)
        : [...prev, chapterId]
    );
  }, []);

  // Expand all chapters
  const handleExpandAll = useCallback(() => {
    setExpandedChapters(DEFAULT_MANUAL_STRUCTURE.map(c => c.id));
  }, []);

  // Collapse all chapters
  const handleCollapseAll = useCallback(() => {
    setExpandedChapters([]);
  }, []);

  // AI Suggest content for a section
  const handleAISuggest = useCallback(async (section) => {
    if (!token || !clientId || generatingSection) return;
    
    setGeneratingSection(section.id);
    toast.info(`Generating content for "${section.title}"...`);
    
    try {
      const response = await api.post(token, `/clients/${clientId}/continuity-manual/ai-suggest`, {
        sectionId: section.id,
        sectionTitle: section.title,
        sectionPrompt: section.prompt
      });
      
      if (response.data?.suggestedContent) {
        // Update the section with AI-generated content
        setSections(prev => ({
          ...prev,
          [section.id]: { content: response.data.suggestedContent }
        }));
        setHasChanges(true);
        setSaveStatus(null);
        toast.success('AI content generated! Review and edit as needed.');
      }
    } catch (error) {
      console.error('AI suggestion failed:', error);
      toast.error('Failed to generate AI content. Please try again.');
    }
    
    setGeneratingSection(null);
  }, [token, clientId, generatingSection]);

  // Save manual
  const handleSave = async () => {
    if (!token || !clientId) return;
    setSaving(true);
    try {
      await api.put(token, `/clients/${clientId}/continuity-manual`, {
        sections,
        hiddenSections
      });
      setSaveStatus('saved');
      setHasChanges(false);
      setLastSaved(new Date().toISOString());
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Failed to save continuity manual:', error);
      setSaveStatus('error');
    }
    setSaving(false);
  };

  // Export to PDF
  const handleExportPDF = async () => {
    try {
      toast.info('Generating PDF...');
      
      // Create PDF content
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Please allow pop-ups to export PDF');
        return;
      }

      const clientName = client?.name || 'Client';
      const exportDate = new Date().toLocaleDateString('en-AU', { 
        day: 'numeric', month: 'long', year: 'numeric' 
      });

      // Build HTML content for PDF
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Continuity Manual - ${clientName}</title>
          <style>
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
            body {
              font-family: 'Georgia', serif;
              line-height: 1.6;
              color: #2C2420;
              max-width: 800px;
              margin: 0 auto;
              padding: 40px;
            }
            .cover {
              text-align: center;
              padding: 100px 40px;
              page-break-after: always;
            }
            .cover h1 {
              font-size: 36px;
              color: #4A3B32;
              margin-bottom: 10px;
            }
            .cover .client-name {
              font-size: 28px;
              color: #2C2420;
              margin-bottom: 40px;
            }
            .cover .date {
              color: #8C8078;
              font-size: 14px;
            }
            .cover .confidential {
              margin-top: 60px;
              padding: 10px 20px;
              background: #F5F1EB;
              display: inline-block;
              font-size: 12px;
              text-transform: uppercase;
              letter-spacing: 1px;
              color: #5D524D;
            }
            h2 {
              color: #4A3B32;
              border-bottom: 2px solid #E6E0D9;
              padding-bottom: 10px;
              margin-top: 40px;
              page-break-after: avoid;
            }
            h3 {
              color: #2C2420;
              margin-top: 25px;
              page-break-after: avoid;
            }
            .section-content {
              background: #FAFAFA;
              padding: 15px 20px;
              border-left: 3px solid #4A3B32;
              margin: 10px 0 25px 0;
              white-space: pre-wrap;
            }
            .empty-section {
              color: #B8AFA8;
              font-style: italic;
            }
            .toc {
              page-break-after: always;
            }
            .toc h2 {
              border-bottom: none;
            }
            .toc ul {
              list-style: none;
              padding: 0;
            }
            .toc li {
              padding: 8px 0;
              border-bottom: 1px dotted #E6E0D9;
            }
            .footer {
              margin-top: 60px;
              padding-top: 20px;
              border-top: 1px solid #E6E0D9;
              text-align: center;
              font-size: 12px;
              color: #8C8078;
            }
          </style>
        </head>
        <body>
          <div class="cover">
            <h1>Continuity Manual</h1>
            <div class="client-name">${clientName}</div>
            <div class="date">Generated: ${exportDate}</div>
            <div class="confidential">Confidential</div>
          </div>
          
          <div class="toc">
            <h2>Table of Contents</h2>
            <ul>
              ${DEFAULT_MANUAL_STRUCTURE.map(chapter => `<li>${chapter.title}</li>`).join('')}
            </ul>
          </div>
      `;

      // Add each chapter and section
      DEFAULT_MANUAL_STRUCTURE.forEach(chapter => {
        // Skip chapters where all sections are hidden
        const visibleSections = chapter.sections.filter(s => !hiddenSections.includes(s.id));
        if (visibleSections.length === 0) return;

        htmlContent += `<h2>${chapter.title}</h2>`;
        
        visibleSections.forEach(section => {
          const content = sections[section.id]?.content?.trim();
          htmlContent += `<h3>${section.title}</h3>`;
          if (content) {
            htmlContent += `<div class="section-content">${content.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>`;
          } else {
            htmlContent += `<div class="section-content empty-section">${section.prompt}</div>`;
          }
        });
      });

      htmlContent += `
          <div class="footer">
            <p>This document is confidential and intended for internal use only.</p>
            <p>QIndex One - Client Information Hub</p>
          </div>
        </body>
        </html>
      `;

      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      // Wait for content to load then print
      printWindow.onload = () => {
        printWindow.print();
      };
      
      toast.success('PDF ready for download');
    } catch (error) {
      console.error('Failed to export PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  // Calculate progress
  const totalSections = DEFAULT_MANUAL_STRUCTURE.reduce((acc, ch) => acc + ch.sections.length, 0);
  const completedSections = Object.values(sections).filter(s => s?.content?.trim()).length;
  const progressPercent = Math.round((completedSections / totalSections) * 100);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#4A3B32] mx-auto mb-4" />
          <p className="text-[#5D524D]">Loading continuity manual...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl" data-testid="continuity-manual-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-2xl font-heading text-[#2C2420] mb-2">Continuity Manual</h1>
            <p className="text-[#5D524D]">
              A comprehensive guide to help new team members understand {client?.name || 'this client'}.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportPDF}
              className="text-xs"
              data-testid="export-pdf-btn"
            >
              <Download className="w-3 h-3 mr-1" />
              Export PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExpandAll}
              className="text-xs"
              data-testid="expand-all-btn"
            >
              Expand All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCollapseAll}
              className="text-xs"
              data-testid="collapse-all-btn"
            >
              Collapse All
            </Button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-[#F5F1EB] rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-[#5D524D]">Manual Completion</span>
            <span className="text-sm font-medium text-[#2C2420]">{progressPercent}%</span>
          </div>
          <div className="h-2 bg-[#E6E0D9] rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#4A3B32] rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="text-xs text-[#8C8078] mt-2">
            {completedSections} of {totalSections} sections completed
            {hiddenSections.length > 0 && ` • ${hiddenSections.length} sections hidden`}
          </p>
        </div>

        {/* Save Bar */}
        <div className="flex items-center justify-between bg-white border border-[#E6E0D9] rounded-lg px-4 py-3">
          <div className="flex items-center gap-3">
            {saveStatus === 'saved' && (
              <span className="flex items-center gap-1 text-sm text-green-600">
                <Check className="w-4 h-4" />
                Saved successfully
              </span>
            )}
            {saveStatus === 'error' && (
              <span className="flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="w-4 h-4" />
                Failed to save
              </span>
            )}
            {!saveStatus && hasChanges && (
              <span className="text-sm text-[#8C8078]">Unsaved changes</span>
            )}
            {!saveStatus && !hasChanges && lastSaved && (
              <span className="text-sm text-[#8C8078]">
                Last saved: {new Date(lastSaved).toLocaleDateString('en-AU', { 
                  day: 'numeric', 
                  month: 'short', 
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            )}
          </div>
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-[#4A3B32] hover:bg-[#3A2E26] text-white"
            data-testid="save-manual-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Manual
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-[#4A3B32]/5 border border-[#4A3B32]/10 rounded-lg p-4 mb-6">
        <div className="flex gap-3">
          <FileText className="w-5 h-5 text-[#4A3B32] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-[#2C2420] font-medium mb-1">
              Purpose of this Manual
            </p>
            <p className="text-sm text-[#5D524D]">
              This document captures institutional knowledge about the client to ensure continuity 
              when team members change. Write over the grey prompts with your content. Use the eye 
              icon to hide sections that aren't relevant.
            </p>
          </div>
        </div>
      </div>

      {/* Chapters */}
      {DEFAULT_MANUAL_STRUCTURE.map((chapter) => (
        <Chapter
          key={chapter.id}
          chapter={chapter}
          sections={sections}
          hiddenSections={hiddenSections}
          onSectionChange={handleSectionChange}
          onToggleHide={handleToggleHide}
          isExpanded={expandedChapters.includes(chapter.id)}
          onToggleExpand={() => handleToggleExpand(chapter.id)}
          onAISuggest={handleAISuggest}
          generatingSection={generatingSection}
        />
      ))}
    </div>
  );
}
